import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  makeStyles,
  shorthands,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
  tokens,
  Tooltip
} from '@fluentui/react-components';
import {
  AddRegular,
  CheckmarkCircle16Filled,
  CheckmarkCircle24Regular,
  ChevronLeftRegular,
  DeleteRegular,
  DismissCircle16Regular,
  EditRegular,
  ErrorCircle24Regular,
  InfoRegular
} from '@fluentui/react-icons';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useError } from '../contexts/ErrorContext';
import { useUnifiedApp } from '../contexts/UnifiedAppContext';
import { FederationSettings, PageNameEvent } from '../hooks/eventTracker.types';
import { useAuthGuard } from '../hooks/useAuthGuard';
import useEventTracker from '../hooks/useEventTracker';
import { useGeneralPermissions } from '../hooks/useGeneralPermissions';
import { apiClient } from '../services/apiClient';
import { domainService } from '../services/domainService';
import { Policy, policyService } from '../services/policyService';
import { teamsService } from '../services/teamsService';
import { ElementButton, PageName } from '../tracking/tracking.types';
import { PolicyDisclaimerSection } from '../components/CustomDisclaimers/PolicyDisclaimerSection';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    backgroundColor: '#f8f9fa',
    maxWidth: '100vw',
    overflowY: 'auto',
    overflowX: 'hidden'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.padding('12px', '16px'),
    backgroundColor: '#ffffff',
    ...shorthands.borderBottom('1px', 'solid', '#e0e0e0')
  },
  backButton: {
    minWidth: 'auto',
    ...shorthands.padding('4px', '8px'),
    marginRight: '12px'
  },
  headerContent: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  title: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#1a1a1a',
    margin: 0
  },
  deleteButton: {
    color: tokens.colorPaletteRedForeground1,
    '&:disabled': {
      color: tokens.colorNeutralForegroundDisabled
    }
  },
  editButton: {
    color: tokens.colorNeutralForeground1,
    '&:disabled': {
      color: tokens.colorNeutralForegroundDisabled
    }
  },
  content: {
    ...shorthands.padding('16px', '24px', '16px', '24px'), // Added proper right padding
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('16px'),
    overflowY: 'auto',
    overflowX: 'hidden',
    maxWidth: 'calc(100vw - 48px)', // Account for padding on both sides
    boxSizing: 'border-box',
    width: '100%'
  },
  settingsCard: {
    backgroundColor: '#ffffff',
    ...shorthands.borderRadius('8px'),
    ...shorthands.border('1px', 'solid', '#e0e0e0'),
    ...shorthands.padding('16px')
  },
  settingsHeader: {
    fontSize: '14px',
    fontWeight: '600',
    marginBottom: '12px',
    color: '#1a1a1a'
  },
  policyInfoCard: {
    backgroundColor: '#ffffff',
    ...shorthands.borderRadius('8px'),
    ...shorthands.border('1px', 'solid', '#e0e0e0'),
    ...shorthands.padding('16px'),
    marginBottom: '16px'
  },
  policyInfoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    ...shorthands.gap('12px')
  },
  policyInfoItem: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('4px')
  },
  policyInfoLabel: {
    fontSize: '12px',
    color: '#666666',
    fontWeight: '500',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  policyInfoValue: {
    fontSize: '14px',
    color: '#1a1a1a',
    fontWeight: '600'
  },
  settingsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    ...shorthands.gap('8px'),
    ...shorthands.padding('8px'),
    '@media (max-width: 768px)': {
      gridTemplateColumns: '1fr'
    }
  },
  settingRow: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.padding('6px', '8px'),
    backgroundColor: '#f8f9fa',
    ...shorthands.borderRadius('4px'),
    minWidth: '0',
    overflow: 'hidden'
  },
  settingLabel: {
    fontSize: '11px',
    color: '#666666',
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('4px'),
    flex: '1',
    minWidth: '0',
    overflow: 'hidden'
  },
  settingValue: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('4px'),
    fontSize: '12px',
    fontWeight: '600',
    color: '#1a1a1a'
  },
  checkIcon: {
    color: tokens.colorPaletteGreenForeground1,
    fontSize: '14px'
  },
  closeIcon: {
    color: tokens.colorNeutralForeground3,
    fontSize: '14px'
  },
  infoIcon: {
    color: tokens.colorBrandForeground1,
    fontSize: '14px'
  },
  noDataMessage: {
    ...shorthands.padding('40px'),
    textAlign: 'center',
    color: '#666666',
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.borderRadius('8px')
  },
  syncButton: {
    marginTop: '12px'
  },
  domainsCard: {
    backgroundColor: '#ffffff',
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('16px'),
    ...shorthands.padding('24px'),
    ...shorthands.borderRadius('6px'),
    boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.25)',
    minHeight: 0,
    maxWidth: '100%',
    boxSizing: 'border-box',
    flexShrink: 0
  },
  domainsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    ...shorthands.gap('16px')
  },
  domainsTitleGroup: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('10px'),
    minWidth: 0
  },
  domainsTitle: {
    fontSize: '20px',
    lineHeight: '28px',
    fontWeight: '600',
    color: '#242424'
  },
  domainsCountBadge: {
    height: '24px',
    minWidth: '24px',
    ...shorthands.padding('0', '8px'),
    ...shorthands.borderRadius('99px'),
    backgroundColor: '#f5f5f5',
    color: '#616161',
    fontSize: '12px',
    lineHeight: '16px',
    fontWeight: '600'
  },
  addDomainButton: {
    minHeight: '32px',
    height: '32px',
    ...shorthands.padding('5px', '12px'),
    ...shorthands.borderRadius('4px')
  },
  domainsContent: {
    width: '100%',
    minWidth: 0
  },
  table: {
    width: '100%',
    tableLayout: 'fixed' // Force fixed layout to prevent overflow
  },
  tableHeader: {
    backgroundColor: '#ffffff'
  },
  tableHeaderRow: {
    ...shorthands.borderBottom('1px', 'solid', '#e6e6e6')
  },
  tableHeaderCell: {
    ...shorthands.padding('8px', '12px'),
    fontSize: '12px',
    lineHeight: '16px',
    fontWeight: '600',
    color: '#242424',
    textTransform: 'none',
    letterSpacing: 'normal'
  },
  domainTableRow: {
    height: '49px',
    ...shorthands.borderBottom('1px', 'solid', '#e6e6e6')
  },
  tableCell: {
    ...shorthands.padding('8px', '12px'),
    fontSize: '12px',
    lineHeight: '16px',
    verticalAlign: 'middle',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  domainCell: {
    ...shorthands.padding('8px', '12px'),
    fontSize: '12px',
    lineHeight: '16px',
    verticalAlign: 'middle',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    wordBreak: 'break-word'
  },
  domainName: {
    fontSize: '12px',
    lineHeight: '16px',
    fontWeight: '600',
    color: '#242424',
    display: 'block' // Ensure block display for proper spacing
  },
  domainStatusBadge: {
    height: '20px',
    ...shorthands.padding('0', '6px'),
    ...shorthands.borderRadius('4px'),
    fontSize: '10px',
    lineHeight: '14px',
    fontWeight: '600'
  },
  removeDomainButton: {
    minWidth: '24px',
    width: '24px',
    height: '24px',
    ...shorthands.padding('2px')
  },
  domainSource: {
    fontSize: '12px',
    color: '#666666',
    marginTop: '2px'
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%'
  },
});

export const PolicyDetails: React.FC = () => {
  const styles = useStyles();
  const { id } = useParams<{id: string;}>();
  const navigate = useNavigate();
  const { showError, showWarning, showInfo } = useError();
  const { user } = useAuthGuard();
  const appContext = useUnifiedApp();
  const [loading, setLoading] = useState(true);
  const [policy, setPolicy] = useState<Policy | null>(null);
  const [teamsPolicyData, setTeamsPolicyData] = useState<any>(null);
  const [domains, setDomains] = useState<any[]>([]);
  const [deleting, setDeleting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [messageDialogTitle, setMessageDialogTitle] = useState('');
  const [messageDialogContent, setMessageDialogContent] = useState('');
  const [messageDialogType, setMessageDialogType] = useState<'success' | 'error'>('success');

  // Domain management state
  const [addDomainDialogOpen, setAddDomainDialogOpen] = useState(false);
  const [newDomainName, setNewDomainName] = useState('');
  const [newDomainType, setNewDomainType] = useState<'allowed' | 'blocked'>('allowed');
  const [addingDomain, setAddingDomain] = useState(false);
  const [availableDomains, setAvailableDomains] = useState<any[]>([]);
  const [selectedDomains, setSelectedDomains] = useState<string[]>([]);
  const [loadingAvailableDomains, setLoadingAvailableDomains] = useState(false);
  const [removeDomainDialog, setRemoveDomainDialog] = useState<{open: boolean;domain: string;type: 'allowed' | 'blocked';}>({
    open: false,
    domain: '',
    type: 'allowed'
  });
  const [removingDomain, setRemovingDomain] = useState(false);
  const { loadingPermissions, loadPermissions } = useGeneralPermissions();

  useEffect(() => {
    if (id) {
      loadPermissions();
      loadPolicyDetails();
    }
  }, [id, loadPermissions]);

  const eventTracker = useEventTracker();
  const handleActionOnAppLaunched = async (mappedFederationSettings: FederationSettings) => {
    const tracker: PageNameEvent = {
      pageName: PageName.POLICIES,
      federationSettings: mappedFederationSettings,
      ...(policy?.precedenceRank && { rank: policy?.precedenceRank }),
      allowedDomains: teamsPolicyData.allowedDomains ?? [],
      blockedDomains: teamsPolicyData.blockedDomains ?? []
    };
    await eventTracker.eventTrackOnAppLaunched(tracker);
  };
  const handleActionOnClick = async (elementButton: ElementButton) => {
    await eventTracker.eventTrackOnElementClicked({
      elementName: elementButton,
      elementType: "button",
      pageName: PageName.POLICY_DETAIL
    });
  };

  // Debug: Track when teamsPolicyData changes
  useEffect(() => {
    console.log('📊 TEAMS POLICY DATA STATE:', {
      exists: !!teamsPolicyData,
      data: teamsPolicyData,
      allowedDomains: teamsPolicyData?.allowedDomains,
      blockedDomains: teamsPolicyData?.blockedDomains,
      isAllowedArray: Array.isArray(teamsPolicyData?.allowedDomains),
      isBlockedArray: Array.isArray(teamsPolicyData?.blockedDomains),
      allowedCount: teamsPolicyData?.allowedDomains?.length || 0,
      blockedCount: teamsPolicyData?.blockedDomains?.length || 0
    });

    if (teamsPolicyData && Object.keys(teamsPolicyData).length) {
      const mappedFederationSettings: FederationSettings = {
        assignments: teamsPolicyData.assignments?.groups?.map((item: any) => item.groupId) ?? [],
        enableFederationAccess: teamsPolicyData.enableFederationAccess ?? false,
        enablePublicCloudAccess: teamsPolicyData.enablePublicCloudAccess ?? false,
        enableTeamsSmsAccess: teamsPolicyData.enableTeamsSmsAccess ?? false,
        restrictTeamsConsumerToExternalUserProfiles: teamsPolicyData.restrictTeamsConsumerAccessToExternalUserProfiles ?? false,
        allowedExternalDomains: teamsPolicyData?.teamsPolicyDetails?.allowedExternalDomains ?? 0,
        communicationWithExternalOrganization: teamsPolicyData.communicationWithExternalOrgs || 'OrganizationDefault',
        enableOutsideAccess: teamsPolicyData.enableOutsideAccess ?? false,
        enableTeamsConsumerAccess: teamsPolicyData.enableTeamsConsumerAccess ?? false,
        enablePublicCloudAudioVideoAccess: teamsPolicyData.enablePublicCloudAudioVideoAccess ?? false,
        enableTeamsConsumerInbound: teamsPolicyData.enableTeamsConsumerInbound ?? false,
        enableXmppAccess: teamsPolicyData.enableXmppAccess ?? false,
        bilateralChatsOnly: teamsPolicyData?.federated_bilateral_chats ?? teamsPolicyData?.federatedBilateralChats ?? false,
        blockedExternalDomains: teamsPolicyData?.teamsPolicyDetails?.blockedExternalDomains ?? 0,
        // additional info
        enableAcsFederationAccess: teamsPolicyData.enableAcsFederationAccess ?? false,
        ...(teamsPolicyData.federatedBilateralChats !== undefined && {
          federatedBilateralChats: teamsPolicyData.federatedBilateralChats
        })
      };
      handleActionOnAppLaunched(mappedFederationSettings);
    }
  }, [teamsPolicyData]);

  // Helper function to convert Teams policy to Policy format for display
  const convertTeamsPolicyToPolicy = (teamsPolicy: any): Policy => {
    return {
      id: id!, // Use the id from useParams which is the UUID
      name: teamsPolicy.identity || teamsPolicy.displayName,
      status: 'Active' as const,
      precedenceRank: (teamsPolicy as any).rank || 0,
      managementMode: 'Actively Managed' as const,
      syncWithFeed: false,
      userGroup: (teamsPolicy as any).assignedGroupDisplayName || 'Teams Administrators',
      bilateralChat: (teamsPolicy as any).federated_bilateral_chats ?? (teamsPolicy as any).federatedBilateralChats ?? false,
      includedDomains: Array.isArray(teamsPolicy.allowedDomains) ? teamsPolicy.allowedDomains : [],
      customDomains: [],
      excludedDomains: Array.isArray(teamsPolicy.blockedDomains) ? teamsPolicy.blockedDomains : [],
      associatedTeamsPolicies: [teamsPolicy.identity],
      lastSyncWithTeams: teamsPolicy.syncedAt || new Date().toISOString(),
      lastFeedSync: new Date().toISOString(),
      syncStatus: (teamsPolicy as any).driftStatus === 'drift_detected' ? 'Drift Detected' : 'In Sync' as const,
      createdAt: teamsPolicy.createdDate || new Date().toISOString(),
      updatedAt: teamsPolicy.lastModified || new Date().toISOString(),
      createdBy: 'Teams PowerShell'
    };
  };

  // Helper function to prepare policy data with domains
  const preparePolicyDataWithDomains = (teamsPolicy: any) => {
    return {
      ...teamsPolicy,
      allowedDomains: teamsPolicy.allowedDomains || [],
      blockedDomains: teamsPolicy.blockedDomains || [],
      inherited_domains: teamsPolicy.inherited_domains || [], // Include inherited domains
      // Include group display name from the Teams policy
      groupDisplayName: (teamsPolicy as any).assignedGroupDisplayName || (teamsPolicy as any).groupDisplayName || (teamsPolicy as any).userGroup || 'Not assigned',
      groupId: (teamsPolicy as any).groupId || (teamsPolicy as any).assignedGroupId || (teamsPolicy as any).entraGroupId
    };
  };

  // Helper function to log policy domain data for debugging
  const logPolicyDomainData = (teamsPolicy: any, policyDataWithDomains: any) => {
    console.log('🔍 Policy domain data:', {
      policyName: teamsPolicy.identity || teamsPolicy.displayName || (teamsPolicy as any).name,
      rawAllowedDomains: teamsPolicy.allowedDomains,
      rawBlockedDomains: teamsPolicy.blockedDomains,
      processedAllowedDomains: policyDataWithDomains.allowedDomains,
      processedBlockedDomains: policyDataWithDomains.blockedDomains,
      allowedCount: policyDataWithDomains.allowedDomains?.length || 0,
      blockedCount: policyDataWithDomains.blockedDomains?.length || 0
    });
  };

  // Helper function to load Teams policy directly
  const loadTeamsPolicyDirectly = async () => {
    const teamsPolicy = await teamsService.getTeamsPolicy(id!);

    console.log('✅ Received Teams policy:', {
      identity: teamsPolicy?.identity,
      name: (teamsPolicy as any)?.name,
      allowedDomains: teamsPolicy?.allowedDomains,
      blockedDomains: teamsPolicy?.blockedDomains
    });

    if (teamsPolicy) {
      const convertedPolicy = convertTeamsPolicyToPolicy(teamsPolicy);
      setPolicy(convertedPolicy);

      const policyDataWithDomains = preparePolicyDataWithDomains(teamsPolicy);
      logPolicyDomainData(teamsPolicy, policyDataWithDomains);
      setTeamsPolicyData(policyDataWithDomains);

      // Don't load all domains - the policy already has its domains
      // The domains are in teamsPolicy.allowedDomains and teamsPolicy.blockedDomains
      setDomains([]); // Clear domains since we'll display from teamsPolicy directly
    }
  };

  // Helper function to handle policy not found error
  const handlePolicyNotFound = (teamsError: any) => {
    if (teamsError?.status === 404 || teamsError?.message?.includes('not found')) {
      setLoading(false);
      showError({
        message: `Policy "${id}" does not exist. Redirecting to policies page...`,
        title: 'Policy Not Found',
        severity: 'error'
      });

      // Redirect to policies page after a short delay
      setTimeout(() => {
        navigate('/policies');
      }, 2000);
      return true;
    }
    return false;
  };

  // Helper function to find matching Teams policy
  const findMatchingTeamsPolicy = (teamsPolicies: any[], foundPolicy: Policy) => {
    return teamsPolicies.find((tp) =>
    tp.identity === foundPolicy.name ||
    tp.identity === foundPolicy.name.replace('_OD', '') ||
    tp.displayName === foundPolicy.name
    );
  };

  // Helper function to sync and retry finding Teams policy
  const syncAndRetryFindTeamsPolicy = async (foundPolicy: Policy) => {
    try {
      await teamsService.syncTeamsPolicies();
      // Reload Teams policies after sync
      const teamsPolicies = await teamsService.getTeamsPolicies();
      return findMatchingTeamsPolicy(teamsPolicies, foundPolicy);
    } catch (syncError: any) {
      console.error('Auto-sync failed:', syncError);
      showWarning(
        'Auto-sync with Teams failed. Policy data may be outdated.',
        'Sync Warning'
      );
      return null;
    }
  };

  // Helper function to create policy data for Teams policy match
  const createPolicyDataFromTeamsPolicy = (matchingTeamsPolicy: any, foundPolicy: Policy) => {
    return {
      ...matchingTeamsPolicy,
      allowedDomains: matchingTeamsPolicy.allowedDomains || [],
      blockedDomains: matchingTeamsPolicy.blockedDomains || [],
      inherited_domains: matchingTeamsPolicy.inherited_domains || [], // Include inherited domains
      // Include group display name from the Teams policy or database policy
      groupDisplayName: (matchingTeamsPolicy as any).assignedGroupDisplayName ||
      (matchingTeamsPolicy as any).groupDisplayName ||
      foundPolicy.assignedGroupDisplayName ||
      foundPolicy.userGroup ||
      'Not assigned',
      groupId: (matchingTeamsPolicy as any).groupId ||
      (matchingTeamsPolicy as any).assignedGroupId ||
      foundPolicy.assignedGroupId ||
      foundPolicy.entraGroupId
    };
  };

  // Helper function to create policy data from database policy
  const createPolicyDataFromDbPolicy = (foundPolicy: Policy) => {
    return {
      identity: foundPolicy.name,
      displayName: foundPolicy.name,
      allowedDomains: (foundPolicy as any).allowedDomains || [],
      blockedDomains: (foundPolicy as any).blockedDomains || [],
      inherited_domains: (foundPolicy as any).inherited_domains || [], // Include inherited domains from database
      enableFederationAccess: true,
      allowFederatedUsers: true,
      // Include group display name from database policy
      groupDisplayName: foundPolicy.assignedGroupDisplayName ||
      foundPolicy.userGroup ||
      'Not assigned',
      groupId: foundPolicy.assignedGroupId ||
      foundPolicy.entraGroupId ||
      foundPolicy.groupId
    };
  };

  // Helper function to handle fallback to PostgreSQL policies
  const handleFallbackToPostgresql = async () => {
    const policies = await policyService.getPolicies();
    const foundPolicy = policies.find((p) => p.id === id || p.name === id);

    if (!foundPolicy) return false;

    setPolicy(foundPolicy);

    try {
      let teamsPolicies = await teamsService.getTeamsPolicies();
      let matchingTeamsPolicy = findMatchingTeamsPolicy(teamsPolicies, foundPolicy);

      // If no matching Teams policy found, automatically sync and try again
      if (!matchingTeamsPolicy) {
        matchingTeamsPolicy = await syncAndRetryFindTeamsPolicy(foundPolicy);
      }

      if (matchingTeamsPolicy) {
        const policyDataWithDomains = createPolicyDataFromTeamsPolicy(matchingTeamsPolicy, foundPolicy);
        setTeamsPolicyData(policyDataWithDomains);
      } else {
        // If no Teams policy data, use the database policy data
        const dbPolicyData = createPolicyDataFromDbPolicy(foundPolicy);
        setTeamsPolicyData(dbPolicyData);
      }
    } catch (error: any) {
      console.error('Failed to load Teams policy data:', error);
      showWarning(
        'Unable to load Teams policy data. Some features may be limited.',
        'Data Loading Warning'
      );
    }

    // Don't load all domains - use the domains from the policy
    setDomains([]); // Clear domains since we'll display from policy data directly
    return true;
  };

  // Helper function to handle Teams policy loading with fallback
  const handleTeamsPolicyLoadingWithFallback = async () => {
    // Try to load the Teams policy directly by name/identity
    try {
      await loadTeamsPolicyDirectly();
    } catch (teamsError: any) {
      console.error('Failed to load Teams policy directly:', teamsError);

      // Check if policy doesn't exist (404 error)
      if (handlePolicyNotFound(teamsError)) {
        return;
      }

      // Show warning that we're falling back to cached data
      showWarning(
        'Unable to load latest Teams policy data. Using cached information.',
        'Teams Connection Issue'
      );

      // Fallback: try to load from PostgreSQL policies (legacy support)
      await handleFallbackPolicyLoading();
    }
  };

  // Helper function to handle fallback policy loading
  const handleFallbackPolicyLoading = async () => {
    try {
      const success = await handleFallbackToPostgresql();
      if (!success) {
        throw new Error('Policy not found in fallback source');
      }
    } catch (fallbackError: any) {
      console.error('Failed to load policy from fallback source:', fallbackError);
      showError({
        message: 'Unable to load policy from any available source. The policy may have been deleted.',
        title: 'Policy Not Found',
        severity: 'error',
        onRetry: () => loadPolicyDetails()
      });
    }
  };

  const loadPolicyDetails = async () => {
    try {
      setLoading(true);
      console.log('📋 Loading policy with ID:', id);

      await handleTeamsPolicyLoadingWithFallback();
    } catch (error: any) {
      console.error('Failed to load policy details:', error);
      showError({
        message: error?.message || 'Failed to load policy details',
        title: 'Loading Error',
        severity: 'error',
        details: error?.stack,
        onRetry: () => loadPolicyDetails()
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSyncPolicy = async () => {
    if (!policy) return;

    try {
      setSyncing(true);
      await teamsService.syncTeamsPolicies();
      await loadPolicyDetails(); // Reload to get fresh data
      showInfo('Policy successfully synchronized with Teams', 'Sync Complete');
    } catch (error: any) {
      console.error('Failed to sync policy:', error);
      showError({
        message: error?.message || 'Failed to synchronize policy with Teams',
        title: 'Sync Failed',
        severity: 'error',
        details: error?.stack,
        onRetry: () => handleSyncPolicy()
      });
    } finally {
      setSyncing(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!policy || deleting) return;

    try {
      setDeleting(true);
      // Use policy.id (UUID) for deletion, not the name
      const policyIdentifier = policy.id || id || policy.name; // id from useParams as fallback
      console.log('🗑️ Deleting policy with identifier:', policyIdentifier);

      const result = await policyService.deletePolicy(policyIdentifier);

      if (result.success) {
        // Policy deleted successfully
        setDeleteDialogOpen(false);
        showInfo(`Policy "${policy.name}" has been queued for deletion.`, 'Success');

        // Refresh the context data to update policy counts
        if (appContext?.refreshData) {
          await appContext.refreshData();
        }

        // Navigate back to policies page with state to trigger refresh
        navigate('/policies', { state: { refresh: true, deletedPolicy: policy.name } });
      } else {
        console.error('Delete failed:', result);
        const errorMessage = typeof result.error === 'string' ?
        result.error :
        result.error?.message ?? 'Failed to delete policy';
        setDeleteDialogOpen(false);
        showError({
          message: errorMessage,
          title: 'Delete Failed',
          severity: 'error',
          onRetry: () => handleConfirmDelete()
        });
      }
    } catch (error: any) {
      console.error('Failed to delete policy:', error);
      setDeleteDialogOpen(false);
      showError({
        message: error?.message || 'Failed to delete policy',
        title: 'Delete Failed',
        severity: 'error',
        details: error?.stack,
        onRetry: () => handleConfirmDelete()
      });
    } finally {
      setDeleting(false);
    }
  };

  const getDisplayName = (policyName: string | undefined): string => {
    if (!policyName) return '';
    return policyName.endsWith('_OD') ? policyName.slice(0, -3) : policyName;
  };

  // Check if this is a managed policy that can be edited
  const isManagedPolicy = policy?.name?.endsWith('_OD') ?? false;

  // Check if this is an unmanaged policy that should NOT be deleted
  // Unmanaged policies are those that don't end with _OD
  const isUnmanagedPolicy = !isManagedPolicy;

  // Check if policy is being deleted
  const isDeletingPolicy = (policy as any)?.deletionStatus === 'pending' || (policy as any)?.deletionStatus === 'in_progress';

  // Load available approved domains when dialog opens
  const loadAvailableDomains = async () => {
    try {
      setLoadingAvailableDomains(true);

      // Get all approved domains
      const allDomains = await domainService.getDomains({ status: 'approved' });

      if (allDomains && allDomains.length > 0) {
        // Get currently assigned domains (both allowed and blocked)
        const currentDomains = [
        ...(teamsPolicyData?.allowedDomains || []),
        ...(teamsPolicyData?.blockedDomains || [])].
        map((d) => d.toLowerCase());

        // Filter out domains already assigned to this policy
        const available = allDomains.filter((domain: any) => {
          const domainName = (domain.domain || '').toLowerCase();
          return !currentDomains.includes(domainName);
        });

        setAvailableDomains(available);
        setSelectedDomains([]); // Reset selections
      } else {
        setAvailableDomains([]);
        setSelectedDomains([]);
      }
    } catch (error) {
      console.error('Failed to load available domains:', error);
      showError({
        message: 'Failed to load available domains',
        title: 'Load Error',
        severity: 'error'
      });
    } finally {
      setLoadingAvailableDomains(false);
    }
  };

  // Domain management functions
  const handleAddDomain = async () => {
    if (selectedDomains.length === 0 || !policy) return;

    try {
      setAddingDomain(true);
      handleActionOnClick(ElementButton.SAVE);

      // Debug logging
      console.log('📝 Adding domains to policy:', {
        policyId: policy.id,
        policyName: policy.name,
        selectedDomains,
        type: newDomainType
      });

      const updateData = {
        allowedDomains: teamsPolicyData?.allowedDomains || [],
        blockedDomains: teamsPolicyData?.blockedDomains || []
      };

      if (newDomainType === 'allowed') {
        updateData.allowedDomains = [...updateData.allowedDomains, ...selectedDomains];
      } else {
        updateData.blockedDomains = [...updateData.blockedDomains, ...selectedDomains];
      }

      // Use apiClient which handles authentication
      const result = await apiClient.put(`/policies/${policy.id}/domains`, updateData);

      // Check if the request was successful
      if (result.success || result.data) {
        // Domain added successfully
        // Reset form and reload policy data
        setSelectedDomains([]);
        setAddDomainDialogOpen(false);
        await loadPolicyDetails();

        const domainsText = selectedDomains.length === 1 ? 'Domain' : 'Domains';
        showInfo(`${selectedDomains.length} ${domainsText} added successfully to ${newDomainType} domains`, 'Domains Added');
      } else {
        throw new Error('Failed to update domains');
      }
    } catch (error: any) {
      console.error('Failed to add domain:', error);
      showError({
        message: error.message || 'Failed to add domain',
        title: 'Add Domain Failed',
        severity: 'error'
      });
    } finally {
      setAddingDomain(false);
    }
  };

  // Helper function to handle remove domain errors
  const handleRemoveDomainError = (error: any) => {
    // Check if this is a 403 error for inherited domain protection
    if (error.statusCode === 403 && error.isInherited) {
      showError({
        message: error.message || `Domain "${removeDomainDialog.domain}" was inherited from the parent policy and cannot be removed.`,
        title: 'Cannot Delete Inherited Domain',
        severity: 'error'
      });
      // Close dialog since user can't delete this domain
      setRemoveDomainDialog({ open: false, domain: '', type: 'allowed' });
    } else {
      showError({
        message: error.message || 'Failed to remove domain',
        title: 'Remove Domain Failed',
        severity: 'error'
      });
    }
  };

  // Helper function to create updated domain data for removal
  const createUpdatedDomainData = () => {
    const updateData = {
      allowedDomains: teamsPolicyData?.allowedDomains || [],
      blockedDomains: teamsPolicyData?.blockedDomains || []
    };

    if (removeDomainDialog.type === 'allowed') {
      updateData.allowedDomains = updateData.allowedDomains.filter((d: string) => d !== removeDomainDialog.domain);
    } else {
      updateData.blockedDomains = updateData.blockedDomains.filter((d: string) => d !== removeDomainDialog.domain);
    }

    return updateData;
  };

  const handleRemoveDomain = async () => {
    if (!removeDomainDialog.domain || !policy) return;

    setRemovingDomain(true);
    try {
      const updateData = createUpdatedDomainData();

      console.log('Removing domain:', removeDomainDialog.domain, 'Type:', removeDomainDialog.type);
      console.log('Updated data:', updateData);

      // Use apiClient which handles authentication
      const result = await apiClient.put(`/policies/${policy.id}/domains`, updateData);

      // Check if the request was successful
      if (result.success || result.data) {
        // Domain removed successfully
        showInfo(`Domain "${removeDomainDialog.domain}" removed successfully from ${removeDomainDialog.type} domains`, 'Domain Removed');

        // Reset dialog and reload policy data
        setRemoveDomainDialog({ open: false, domain: '', type: 'allowed' });
        await loadPolicyDetails();
      } else {
        throw new Error('Failed to update domains');
      }
    } catch (error: any) {
      console.error('Failed to remove domain:', error);
      handleRemoveDomainError(error);
    } finally {
      setRemovingDomain(false);
    }
  };

  // Helper function to build assignment display items
  const buildAssignmentItems = (assignments: any) => {
    const items: string[] = [];

    // Add group assignments with a limit for display
    const MAX_DISPLAY_ITEMS = 5;
    if (assignments.groups?.length > 0) {
      items.push(`Groups (${assignments.groups.length}):`);
      const groupsToShow = assignments.groups.slice(0, MAX_DISPLAY_ITEMS);
      groupsToShow.forEach((g: any) => {
        // Prefer displayName, then fallback to a user-friendly message instead of raw ID
        const displayName = g.displayName || (g.groupId ? `Group ${g.groupId.substring(0, 8)}...` : 'Unknown Group');
        // Don't show rank in group assignments - it's shown in precedence rank field
        items.push(`  • ${displayName}`);
      });
      if (assignments.groups.length > MAX_DISPLAY_ITEMS) {
        items.push(`  ... and ${assignments.groups.length - MAX_DISPLAY_ITEMS} more groups`);
      }
    }

    // Add user assignments with a limit for display
    if (assignments.users?.length > 0) {
      if (items.length > 0) items.push(''); // Add spacing
      items.push(`Users (${assignments.users.length}):`);
      const usersToShow = assignments.users.slice(0, MAX_DISPLAY_ITEMS);
      usersToShow.forEach((u: any) => {
        const displayName = u.displayName || u.userPrincipalName || 'Unknown User';
        const typeInfo = u.assignmentType ? ` (${u.assignmentType})` : '';
        items.push(`  • ${displayName}${typeInfo}`);
      });
      if (assignments.users.length > MAX_DISPLAY_ITEMS) {
        items.push(`  ... and ${assignments.users.length - MAX_DISPLAY_ITEMS} more users`);
      }
    }

    return items;
  };

  // Helper function to get policy assignment display value
  const getPolicyAssignmentValue = () => {
    // Special handling for Global policy
    if (policy?.name === 'Global' || id === 'Global' || (policy as any)?.isGlobalPolicy || (teamsPolicyData as any)?.isGlobalPolicy) {
      return 'Global policy (applies to all users without a specific policy assigned)';
    }

    // Check if we have assignment data from the new structure
    const assignments = (policy as any)?.assignments || (teamsPolicyData as any)?.assignments;

    // If we have multiple assignments, display them
    if (assignments?.groups?.length > 0 || assignments?.users?.length > 0) {
      const items = buildAssignmentItems(assignments);
      return items.join('\n');
    }

    // Fallback to legacy single assignment display
    const groupDisplay = teamsPolicyData.groupDisplayName || teamsPolicyData.groupId || policy?.groupId;

    if (groupDisplay) {
      // Don't show rank in group assignments - it's shown in precedence rank field
      return groupDisplay;
    }

    return 'Not assigned';
  };

  // Helper function to render setting icon based on setting type
  const renderSettingIcon = (setting: any) => {
    if (setting.isText) {
      return <InfoRegular className={styles.infoIcon} data-testid="policy-details-setting-info-regular" />;
    }

    if (setting.isCount) {
      return setting.value > 0 ?
      <CheckmarkCircle16Filled className={styles.checkIcon} data-testid="policy-details-count-checkmark-circle16-filled" /> :

      <DismissCircle16Regular className={styles.closeIcon} data-testid="policy-details-count-dismiss-circle16-regular" />;

    }

    return setting.value ?
    <CheckmarkCircle16Filled className={styles.checkIcon} data-testid="policy-details-boolean-checkmark-circle16-filled" /> :

    <DismissCircle16Regular className={styles.closeIcon} data-testid="policy-details-boolean-dismiss-circle16-regular" />;

  };

  // Helper function to render setting text content
  const renderSettingText = (setting: any) => {
    if (!setting.isText) return null;

    if (setting.isMultiline && setting.value && setting.value !== 'Not assigned') {
      return (
        <Tooltip
          content={{
            children:
            <div style={{
              whiteSpace: 'pre-wrap',
              maxWidth: '400px',
              maxHeight: '400px',
              overflowY: 'auto',
              padding: '8px'
            }} data-testid="policy-details-setting-tooltip-content-div">
                {setting.value}
              </div>

          }}
          relationship="description" data-testid="policy-details-setting-tooltip">

          <div className={styles.settingValue} style={{
            marginTop: '2px',
            fontSize: '12px',
            fontWeight: '400',
            cursor: 'help',
            textDecoration: 'underline dotted',
            textUnderlineOffset: '2px'
          }} data-testid="policy-details-setting-value-div">
            {setting.value.split('\n')[0] || 'Not configured'}
            {setting.value.includes('\n') && ' (hover for details)'}
          </div>
        </Tooltip>);

    }

    return (
      <div className={styles.settingValue} style={{ marginTop: '2px', fontSize: '12px', fontWeight: '400' }} data-testid="policy-details-setting-value-2-div">
        {setting.value || 'Not configured'}
      </div>);

  };

  // Helper function to get policy settings configuration
  const getPolicySettingsConfig = () => {
    // Get all PowerShell policy attributes with comprehensive mapping
    return [
    {
      key: 'PolicyAssignments',
      label: 'Policy Assignments',
      value: getPolicyAssignmentValue(),
      isText: true,
      isMultiline: true // Add this to handle multi-line display
    },
    {
      key: 'CommunicationWithExternalOrgs',
      label: 'Communication with external organizations',
      value: teamsPolicyData.communicationWithExternalOrgs || teamsPolicyData.CommunicationWithExternalOrgs || 'Not configured',
      isText: true
    },
    {
      key: 'EnableFederationAccess',
      label: 'Enable federation access',
      value: teamsPolicyData.allowFederatedUsers ?? teamsPolicyData.enableFederationAccess ?? teamsPolicyData.EnableFederationAccess ?? false
    },
    {
      key: 'EnableOutsideAccess',
      label: 'Enable outside access',
      value: teamsPolicyData.enableOutsideAccess ?? teamsPolicyData.EnableOutsideAccess ?? false
    },
    {
      key: 'EnablePublicCloudAccess',
      label: 'Enable public cloud access',
      value: teamsPolicyData.enablePublicCloudAccess ?? teamsPolicyData.EnablePublicCloudAccess ?? false
    },
    {
      key: 'EnablePublicCloudAudioVideoAccess',
      label: 'Enable public cloud audio/video access',
      value: teamsPolicyData.enablePublicCloudAudioVideoAccess ?? teamsPolicyData.EnablePublicCloudAudioVideoAccess ?? false
    },
    {
      key: 'EnableTeamsConsumerAccess',
      label: 'Enable Teams consumer access',
      value: teamsPolicyData.enableTeamsConsumerAccess ?? teamsPolicyData.EnableTeamsConsumerAccess ?? false
    },
    {
      key: 'EnableTeamsConsumerInbound',
      label: 'Enable Teams consumer inbound',
      value: teamsPolicyData.enableTeamsConsumerInbound ?? teamsPolicyData.EnableTeamsConsumerInbound ?? false
    },
    {
      key: 'EnableTeamsSmsAccess',
      label: 'Enable Teams SMS access',
      value: teamsPolicyData.enableTeamsSmsAccess ?? teamsPolicyData.EnableTeamsSmsAccess ?? false
    },
    {
      key: 'EnableXmppAccess',
      label: 'Enable XMPP access',
      value: teamsPolicyData.enableXmppAccess ?? teamsPolicyData.EnableXmppAccess ?? false
    },
    {
      key: 'RestrictTeamsConsumerAccessToExternalUserProfiles',
      label: 'Restrict Teams consumer to external user profiles',
      value: teamsPolicyData.restrictTeamsConsumerAccessToExternalUserProfiles ?? teamsPolicyData.RestrictTeamsConsumerAccessToExternalUserProfiles ?? false
    },
    {
      key: 'BilateralChat',
      label: 'Bilateral chats only',
      value: teamsPolicyData.federatedBilateralChats ?? teamsPolicyData.FederatedBilateralChats ?? false
    },
    {
      key: 'AllowedDomains',
      label: 'Allowed external domains',
      value: teamsPolicyData.allowedDomains?.length || 0,
      isCount: true,
      domains: teamsPolicyData.allowedDomains || []
    },
    {
      key: 'BlockedDomains',
      label: 'Blocked external domains',
      value: teamsPolicyData.blockedDomains?.length || 0,
      isCount: true,
      isBlocked: true,
      domains: teamsPolicyData.blockedDomains || []
    }];

  };

  // Helper function to render policy settings from Teams data
  const renderPolicySettings = () => {
    if (!teamsPolicyData) {
      return (
        <div className={styles.noDataMessage} data-testid="policy-details-no-data-message-div">
          <InfoRegular style={{ fontSize: '24px', marginBottom: '8px' }} data-testid="policy-details-no-data-info-regular" />
          <Text size={300} block data-testid="policy-details-teams-policy-details-not-available-text">
            Teams policy details not available.
          </Text>
          <Text size={200} block style={{ marginTop: '8px' }} data-testid="policy-details-sync-with-teams-to-fetch-the-text">
            Sync with Teams to fetch the latest policy configuration.
          </Text>
          <Button
            appearance="primary"
            className={styles.syncButton}
            onClick={handleSyncPolicy}
            disabled={syncing} data-testid="policy-details-sync-button">

            {syncing ? 'Syncing...' : 'Sync with Teams'}
          </Button>
        </div>);

    }

    const settings = getPolicySettingsConfig();

    return (
      <div className={styles.settingsGrid} data-testid="policy-details-settings-grid-div">
        {settings.map((setting) => {
          // Don't skip any settings - show all Teams policy attributes
          return (
            <div key={setting.key} className={styles.settingRow} data-testid="policy-details-setting-row-div">
              <div className={styles.settingLabel} data-testid="policy-details-setting-label-div">
                {renderSettingIcon(setting)}

                <span style={{ marginLeft: '8px' }} data-testid="policy-details-setting-label-content-span">
                  {setting.label}
                  {renderSettingText(setting)}
                  {setting.isCount &&
                  <Badge
                    appearance="filled"
                    color={setting.isBlocked ? "danger" : setting.value > 0 ? "success" : "subtle"}
                    size="small"
                    style={{ marginLeft: '8px' }} data-testid="policy-details-setting-count-badge">

                      {setting.value || 0}
                    </Badge>
                  }
                </span>
              </div>
            </div>);

        })}
      </div>);

  };

  const addDomainText = (selectedDomains: string[], newDomainType: string): string => {
    return `Add ${selectedDomains.length} ${newDomainType} Domain${selectedDomains.length !== 1 ? 's' : ''}`;
  };

  const deletePolicyText = (isDeletingPolicy: boolean, isUnmanagedPolicy: boolean): string => {
    return isDeletingPolicy ?
    "Policy is currently being deleted" :
    isUnmanagedPolicy ?
    "Cannot delete unmanaged policies. Only policies ending with '_OD' can be deleted." :
    "Delete this policy";
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer} data-testid="policy-details-loading-container-div">
        <Spinner size="large" label="Loading policy details..." data-testid="policy-details-loading-policy-details-spinner" />
      </div>);

  }

  if (!policy) {
    return (
      <div className={styles.container} data-testid="policy-details-container-div">
        <div className={styles.header} data-testid="policy-details-header-div">
          <Button
            appearance="subtle"
            icon={<ChevronLeftRegular data-testid="policy-details-chevron-left-regular" />}
            className={styles.backButton}
            onClick={() => navigate('/policies')} data-testid="policy-details-back-button">

            Back
          </Button>
          <Text data-testid="policy-details-policy-not-found-text">Policy not found</Text>
        </div>
      </div>);

  }

  const LoadingAvailableDomains = () => {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }} data-testid="policy-details-available-domains-loading-div">
        <Spinner size="medium" label="Loading available domains..." data-testid="policy-details-loading-available-domains-spinner" />
      </div>);

  };

  // Helper function to handle domain selection changes
  const handleDomainSelectionChange = (domainName: string, isChecked: boolean) => {
    if (isChecked) {
      setSelectedDomains([...selectedDomains, domainName]);
    } else {
      setSelectedDomains(selectedDomains.filter((d) => d !== domainName));
    }
  };

  const AvailableDomains = () => {
    return (
      <>
        {availableDomains.length === 0 ?
        <div style={{ padding: '20px', textAlign: 'center', color: '#666' }} data-testid="policy-details-available-domains-empty-state-div">
            <Text data-testid="policy-details-no-approved-domains-available-to-add-text">No approved domains available to add.</Text>
            <br data-testid="policy-details-available-domains-empty-break-br" />
            <Text style={{ fontSize: '12px', marginTop: '8px' }} data-testid="policy-details-all-approved-domains-are-already-assigned-text">
              All approved domains are already assigned to this policy.
            </Text>
          </div> :

        <div style={{
          maxHeight: '400px',
          overflowY: 'auto',
          border: '1px solid #e0e0e0',
          borderRadius: '4px',
          padding: '8px'
        }} data-testid="policy-details-available-domains-list-div">
            {availableDomains.map((domain: any) => {
            const domainName = domain.domain || '';
            const organization = domain.organization ||
            domain.metadata?.organization ||
            'Unknown';
            return (
              <div key={domainName} style={{
                padding: '8px',
                borderBottom: '1px solid #f0f0f0',
                display: 'flex',
                alignItems: 'center'
              }} data-testid="policy-details-available-domain-item-div">
                  <Checkbox
                  checked={selectedDomains.includes(domainName)}
                  onChange={(e, data) => handleDomainSelectionChange(domainName, Boolean(data.checked))}
                  disabled={addingDomain}
                  label={
                  <div data-testid="policy-details-available-domain-label-div">
                        <Text style={{ fontWeight: '500' }} data-testid="policy-details-available-domain-name-text">{domainName}</Text>
                        <br data-testid="policy-details-available-domain-label-break-br" />
                        <Text style={{ fontSize: '12px', color: '#666' }} data-testid="policy-details-available-domain-organization-text">
                          {organization}
                        </Text>
                      </div>
                  } data-testid="policy-details-checkbox" />

                </div>);

          })}
          </div>
        }
      </>);

  };

  const DeleteconfirmationDialog = () => {
    return (
      <Dialog
        open={deleteDialogOpen}
        onOpenChange={(event, data) => {
          // Prevent closing while processing
          if (!deleting) {
            setDeleteDialogOpen(data.open);
          }
        }}
        modalType={deleting ? "modal" : "non-modal"} data-testid="policy-details-delete-confirmation-dialog">

        <DialogSurface data-testid="policy-details-delete-confirmation-dialog-surface">
          <DialogBody data-testid="policy-details-delete-confirmation-dialog-body">
            <DialogTitle data-testid="policy-details-confirm-deletion-dialog-title">Confirm Deletion</DialogTitle>
            <DialogContent data-testid="policy-details-delete-confirmation-dialog-content">
              Are you sure you want to delete the policy "{policy?.name || 'this policy'}"?
              This action cannot be undone.
            </DialogContent>
            <DialogActions data-testid="policy-details-delete-confirmation-dialog-actions">
              <DialogTrigger disableButtonEnhancement data-testid="policy-details-delete-confirmation-dialog-trigger">
                <Button appearance="secondary" disabled={deleting} data-testid="policy-details-delete-confirmation-cancel-button">Cancel</Button>
              </DialogTrigger>
              <Button
                appearance="primary"
                onClick={handleConfirmDelete}
                disabled={deleting} data-testid="policy-details-delete-confirmation-button">

                {deleting ? 'Processing...' : 'Delete'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>);

  };

  const MessageDialog = () => {
    return (
      <Dialog
        open={messageDialogOpen}
        onOpenChange={(event, data) => setMessageDialogOpen(data.open)} data-testid="policy-details-message-dialog-container">

        <DialogSurface data-testid="policy-details-message-dialog-surface">
          <DialogBody data-testid="policy-details-message-dialog-body">
            <DialogTitle data-testid="policy-details-dialog-title">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }} data-testid="policy-details-message-dialog-title-div">
                {messageDialogType === 'success' ?
                <CheckmarkCircle24Regular style={{ color: tokens.colorPaletteGreenForeground1 }} data-testid="policy-details-checkmark-circle24-regular" /> :

                <ErrorCircle24Regular style={{ color: tokens.colorPaletteRedForeground1 }} data-testid="policy-details-error-circle24-regular" />
                }
                {messageDialogTitle}
              </div>
            </DialogTitle>
            <DialogContent data-testid="policy-details-message-dialog-content">
              {messageDialogContent}
            </DialogContent>
            <DialogActions data-testid="policy-details-message-dialog-actions">
              <Button
                appearance="primary"
                onClick={() => setMessageDialogOpen(false)} data-testid="policy-details-ok-button">

                OK
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>);

  };

  return (
    <div className={styles.container} data-testid="policy-details-container-2-div">
      <div className={styles.header} data-testid="policy-details-header-2-div">
        <Button
          appearance="subtle"
          icon={<ChevronLeftRegular data-testid="policy-details-header-chevron-left-regular" />}
          className={styles.backButton}
          onClick={() => navigate('/policies')} data-testid="policy-details-policies-button">

          Policies
        </Button>
        <div className={styles.headerContent} data-testid="policy-details-header-content-div">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }} data-testid="policy-details-header-title-row-div">
            <Text className={styles.title} data-testid="policy-details-policy-name-text">{policy?.name || ''}</Text>
            {(() => {
              // Get rank from multiple sources
              const assignments = (policy as any)?.assignments || (teamsPolicyData as any)?.assignments;
              const rank = (policy as any)?.precedenceRank ||
              teamsPolicyData?.rank || (
              assignments?.groups && assignments.groups.length > 0 ? assignments.groups[0].rank : null);

              // Only show rank if it's a valid number (not null/undefined and not the Global policy)
              if (rank !== null && rank !== undefined && policy?.name !== 'Global') {
                return (
                  <div style={{
                    padding: '6px 12px',
                    backgroundColor: '#f0f8ff',
                    borderRadius: '4px',
                    border: '1px solid #0078d4',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }} data-testid="policy-details-priority-badge-div">
                    <Text size={200} style={{ color: '#0078d4', fontWeight: '600' }} data-testid="policy-details-priority-text">
                      Priority: {rank}
                    </Text>
                  </div>);

              }
              return null;
            })()}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button
              appearance="subtle"
              icon={<EditRegular data-testid="policy-details-header-edit-regular" />}
              className={styles.editButton}
              onClick={() => navigate(`/policies/${id}/edit`)}
              data-testid="policy-details-edit-policy-button"
            >
              Edit policy
            </Button>
            <Button
              appearance="subtle"
              icon={loadingPermissions || isDeletingPolicy ? <Spinner size="tiny" data-testid="policy-details-delete-spinner" /> : <DeleteRegular data-testid="policy-details-header-delete-regular" />}
              className={styles.deleteButton}
              onClick={() => setDeleteDialogOpen(true)}
              disabled={loadingPermissions || deleting || isUnmanagedPolicy || isDeletingPolicy}
              title={deletePolicyText(isDeletingPolicy, isUnmanagedPolicy)}
              data-testid="policy-details-delete-policy-button"
            >
              {isDeletingPolicy ? 'Deleting...' : 'Delete policy'}
            </Button>
          </div>
        </div>
      </div>

      <div className={styles.content} data-testid="policy-details-content-div">
        {/* Deletion Warning Banner */}
        {isDeletingPolicy &&
        <div style={{
          backgroundColor: '#fef0f0',
          border: '1px solid #d13438',
          borderRadius: '4px',
          padding: '12px 16px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }} data-testid="policy-details-deletion-warning-banner-div">
            <div style={{
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            border: '2px solid #d13438',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '12px',
            fontWeight: 'bold',
            color: '#d13438'
          }} data-testid="policy-details-deletion-warning-icon-div">!</div>
            <div data-testid="policy-details-deletion-warning-content-div">
              <Text weight="semibold" style={{ color: '#d13438' }} data-testid="policy-details-policy-deletion-in-progress-text">
                Policy Deletion In Progress
              </Text>
              <Text style={{ fontSize: '12px', color: '#605e5c', marginTop: '4px' }} data-testid="policy-details-this-policy-is-currently-being-deleted-text">
                This policy is currently being deleted. All modification actions are disabled.
              </Text>
            </div>
          </div>
        }

        {/* Policy Settings Card - Compact */}
        <div className={styles.settingsCard} data-testid="policy-details-settings-card-div">
          <Text className={styles.settingsHeader} data-testid="policy-details-federation-settings-text">Federation Settings</Text>
          {renderPolicySettings()}
        </div>

        <PolicyDisclaimerSection />

        {/* Domains Table */}

        <div
          className={styles.domainsCard}
          style={{
            padding: '24px',
            gap: '16px',
            border: 'none',
            borderRadius: '6px',
            boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.25)',
            minHeight: 0
          }}
          data-testid="policy-details-domains-card-div"
        >
          <div
            className={styles.domainsHeader}
            style={{ padding: 0, borderBottom: 'none' }}
            data-testid="policy-details-domains-header-div"
          >
            <div className={styles.domainsTitleGroup}>
             <Text
               className={styles.domainsTitle}
               style={{ fontSize: '20px', lineHeight: '28px' }}
               data-testid="policy-details-domains-text"
             >
               Domains
             </Text>
             {(teamsPolicyData?.allowedDomains?.length || 0) + (teamsPolicyData?.blockedDomains?.length || 0) > 0 &&
             <Badge
               appearance="filled"
               color="subtle"
               className={styles.domainsCountBadge}
               style={{ height: '24px', minWidth: '24px' }}
               data-testid="policy-details-domains-count-badge"
             >
                  {(teamsPolicyData?.allowedDomains?.length || 0) + (teamsPolicyData?.blockedDomains?.length || 0)}
             </Badge>
              }
            </div>
            {isManagedPolicy &&
            <Button
              appearance="secondary"
              size="small"
              className={styles.addDomainButton}
              icon={
                <AddRegular
                  style={{ width: '20px', height: '20px' }}
                  data-testid="policy-details-add-regular"
                />
              }
              onClick={async () => {
                setNewDomainType('allowed');
                setAddDomainDialogOpen(true);
                await loadAvailableDomains();
              }}
              disabled={isDeletingPolicy}
              title={isDeletingPolicy ? "Cannot add domains to policy being deleted" : undefined} data-testid="policy-details-add-allowed-domain-button">

                Add allowed domain
              </Button>
            }
          </div>
          <div className={styles.domainsContent} data-testid="policy-details-domains-content-div">
            <Table className={styles.table} data-testid="policy-details-table">
              <TableHeader className={styles.tableHeader} data-testid="policy-details-table-header">
               <TableRow className={styles.tableHeaderRow} data-testid="policy-details-domains-table-header-row">
                 <TableHeaderCell
                   className={styles.tableHeaderCell}
                   style={{ width: '52%', fontSize: '12px', lineHeight: '16px', textTransform: 'none' }}
                   data-testid="policy-details-domain-table-header-cell"
                 >
                   Domain
                 </TableHeaderCell>
                 <TableHeaderCell
                   className={styles.tableHeaderCell}
                   style={{ width: '23%' }}
                   data-testid="policy-details-type-table-header-cell"
                 >
                   Type
                 </TableHeaderCell>
                 <TableHeaderCell
                   className={styles.tableHeaderCell}
                   style={{ width: '25%' }}
                   data-testid="policy-details-actions-table-header-cell"
                 >
                   Actions
                 </TableHeaderCell>
               </TableRow>
              </TableHeader>
              <TableBody data-testid="policy-details-table-body">
                {/* Show allowed domains from Teams policy */}
                {teamsPolicyData && Array.isArray(teamsPolicyData.allowedDomains) && teamsPolicyData.allowedDomains.map((domain: string) => {
                  const isInherited = (teamsPolicyData.inherited_domains || []).includes(domain);
                  return (
                    <TableRow
                      key={`allowed-${domain}`}
                      className={styles.domainTableRow}
                      style={{ height: '49px' }}
                      data-testid="policy-details-allowed-domain-table-row"
                    >
                      <TableCell className={styles.domainCell} data-testid="policy-details-allowed-domain-table-cell">
                        <div
                          className={styles.domainName}
                          style={{ fontSize: '12px', lineHeight: '16px', fontWeight: 600 }}
                          data-testid="policy-details-domain-name-div"
                        >
                          {domain}
                        </div>
                      </TableCell>
                      <TableCell className={styles.tableCell} data-testid="policy-details-allowed-type-table-cell">
                        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }} data-testid="policy-details-domain-type-badges-div">
                          <Badge
                            appearance="filled"
                            color="success"
                            size="small"
                            className={styles.domainStatusBadge}
                            style={{ height: '20px', fontSize: '10px', lineHeight: '14px' }}
                            data-testid="policy-details-allowed-badge"
                          >
                            Allowed
                          </Badge>
                          {isInherited &&
                          <Badge
                            appearance="filled"
                            color="informative"
                            size="small"
                            className={styles.domainStatusBadge}
                            data-testid="policy-details-inherited-badge"
                          >
                            Inherited
                          </Badge>
                          }
                        </div>
                      </TableCell>
                      <TableCell className={styles.tableCell} data-testid="policy-details-allowed-actions-table-cell">
                        {isManagedPolicy ?
                        <Tooltip
                          content={
                          isDeletingPolicy ?
                          "Cannot remove domains from policy being deleted" :
                          isInherited ?
                          "Cannot delete inherited domain from parent policy" :
                          "Remove domain"
                          }
                          relationship="label" data-testid="policy-details-allowed-domain-remove-tooltip">

                            <Button
                            appearance="subtle"
                            size="small"
                            className={styles.removeDomainButton}
                            icon={
                              <span style={{ display: 'inline-flex', width: '20px', height: '20px', minWidth: '20px' }}>
                                <DeleteRegular
                                  style={{ width: '20px', height: '20px' }}
                                  data-testid="policy-details-allowed-domain-delete-regular"
                                />
                              </span>
                            }
                            onClick={() => setRemoveDomainDialog({
                              open: true,
                              domain,
                              type: 'allowed'
                            })}
                            disabled={isInherited || isDeletingPolicy}
                            style={{ minWidth: '24px', width: '24px', height: '24px' }}
                            aria-label={
                            isDeletingPolicy ?
                            "Cannot remove domain - policy is deleting" :
                            isInherited ?
                            "Cannot remove inherited domain" :
                            "Remove domain"
                            } data-testid="policy-details-remove-allowed-domain-button" />

                          </Tooltip> :
                        null}
                      </TableCell>
                    </TableRow>);

                })}

                {/* Show blocked domains from Teams policy */}
                {teamsPolicyData && Array.isArray(teamsPolicyData.blockedDomains) && teamsPolicyData.blockedDomains.map((domain: string) =>
                <TableRow
                  key={`blocked-${domain}`}
                  className={styles.domainTableRow}
                  style={{ height: '49px' }}
                  data-testid="policy-details-blocked-domain-table-row"
                >
                    <TableCell className={styles.domainCell} data-testid="policy-details-blocked-domain-table-cell">
                      <div
                        className={styles.domainName}
                        style={{ fontSize: '12px', lineHeight: '16px', fontWeight: 600 }}
                        data-testid="policy-details-domain-name-2-div"
                      >
                        {domain}
                      </div>
                    </TableCell>
                    <TableCell className={styles.tableCell} data-testid="policy-details-blocked-type-table-cell">
                      <Badge
                        appearance="filled"
                        color="danger"
                        size="small"
                        className={styles.domainStatusBadge}
                        data-testid="policy-details-blocked-badge"
                      >
                        Blocked
                      </Badge>
                    </TableCell>
                    <TableCell className={styles.tableCell} data-testid="policy-details-blocked-actions-table-cell">
                      {isManagedPolicy ?
                    <Tooltip
                      content={isDeletingPolicy ? "Cannot remove domains from policy being deleted" : "Remove domain"}
                      relationship="label" data-testid="policy-details-blocked-domain-remove-tooltip">

                          <Button
                        appearance="subtle"
                        size="small"
                        className={styles.removeDomainButton}
                        icon={
                          <span style={{ display: 'inline-flex', width: '20px', height: '20px', minWidth: '20px' }}>
                            <DeleteRegular
                              style={{ width: '20px', height: '20px' }}
                              data-testid="policy-details-blocked-domain-delete-regular"
                            />
                          </span>
                        }
                        onClick={() => setRemoveDomainDialog({
                          open: true,
                          domain,
                          type: 'blocked'
                        })}
                        disabled={isDeletingPolicy}
                        style={{ minWidth: '24px', width: '24px', height: '24px' }}
                        aria-label={isDeletingPolicy ? "Cannot remove domain - policy is deleting" : "Remove domain"} data-testid="policy-details-remove-blocked-domain-button" />

                        </Tooltip> :
                    null}
                    </TableCell>
                  </TableRow>
                )}

                {/* Show domains from domain service (ADM managed) */}
                {domains.map((domain: any, index: number) =>
                <TableRow
                  key={`domain-${domain.domain || index}`}
                  className={styles.domainTableRow}
                  style={{ height: '49px' }}
                  data-testid="policy-details-adm-domain-table-row"
                >
                    <TableCell className={styles.domainCell} data-testid="policy-details-adm-domain-table-cell">
                      <div
                        className={styles.domainName}
                        style={{ fontSize: '12px', lineHeight: '16px', fontWeight: 600 }}
                        data-testid="policy-details-domain-name-3-div"
                      >
                        {domain.domain}
                      </div>
                    </TableCell>
                    <TableCell className={styles.tableCell} data-testid="policy-details-adm-type-table-cell">
                      <Badge
                        appearance="filled"
                        color="informative"
                        size="small"
                        className={styles.domainStatusBadge}
                        data-testid="policy-details-adm-badge"
                      >
                        ADM
                      </Badge>
                    </TableCell>
                    <TableCell className={styles.tableCell} data-testid="policy-details-adm-actions-table-cell">
                      {/* ADM domains don't have remove button */}
                    </TableCell>
                  </TableRow>
                )}

                {/* Show empty state if no domains */}
                {!teamsPolicyData?.allowedDomains?.length && !teamsPolicyData?.blockedDomains?.length && domains.length === 0 &&
                <TableRow data-testid="policy-details-empty-domains-table-row">
                    <TableCell colSpan={3} className={styles.tableCell} data-testid="policy-details-empty-domains-table-cell">
                      <div style={{ textAlign: 'center', padding: '24px', color: '#666666' }} data-testid="policy-details-domains-empty-state-div">
                        No domains configured for this policy
                      </div>
                    </TableCell>
                  </TableRow>
                }
              </TableBody>
            </Table>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <DeleteconfirmationDialog data-testid="policy-details-deleteconfirmation-dialog" />

      {/* Message Dialog for Success/Error */}
      <MessageDialog data-testid="policy-details-message-dialog" />

      {/* Add Domain Dialog */}
      <Dialog
        open={addDomainDialogOpen}
        onOpenChange={(event, data) => {
          // Prevent closing while processing
          if (!addingDomain) {
            setAddDomainDialogOpen(data.open);
          }
        }}
        modalType={addingDomain ? "modal" : "non-modal"} data-testid="policy-details-add-domain-dialog">

        <DialogSurface style={{ maxWidth: '600px', maxHeight: '80vh' }} data-testid="policy-details-add-domain-dialog-surface">
          <DialogBody data-testid="policy-details-add-domain-dialog-body">
            <DialogTitle data-testid="policy-details-add-dialog-title">Add {newDomainType} Domains</DialogTitle>
            <DialogContent data-testid="policy-details-add-domain-dialog-content">
              <div style={{ marginBottom: '12px' }} data-testid="policy-details-add-domain-description-div">
                <Text style={{ fontSize: '14px', color: '#666' }} data-testid="policy-details-select-approved-domains-to-add-as-text">
                  Select approved domains to add as {newDomainType} domains:
                </Text>
              </div>
              {loadingAvailableDomains ? <LoadingAvailableDomains data-testid="policy-details-loading-available-domains" /> : <AvailableDomains data-testid="policy-details-available-domains" />}
              {availableDomains.length > 0 &&
              <div style={{ marginTop: '12px', fontSize: '13px', color: '#666' }} data-testid="policy-details-add-domain-selection-summary-div">
                  {selectedDomains.length} of {availableDomains.length} domains selected
                </div>
              }
            </DialogContent>
            <DialogActions data-testid="policy-details-add-domain-dialog-actions">
              <DialogTrigger disableButtonEnhancement data-testid="policy-details-add-domain-dialog-trigger">
                <Button appearance="secondary" disabled={addingDomain} data-testid="policy-details-add-domain-cancel-button">Cancel</Button>
              </DialogTrigger>
              <Button
                appearance="primary"
                onClick={handleAddDomain}
                disabled={addingDomain || selectedDomains.length === 0} data-testid="policy-details-add-domain-button">

                {addingDomain ? 'Adding...' : addDomainText(selectedDomains, newDomainType)}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Remove Domain Dialog */}
      <Dialog
        open={removeDomainDialog.open}
        onOpenChange={(event, data) => {
          // Prevent closing while processing
          if (!removingDomain) {
            setRemoveDomainDialog({ ...removeDomainDialog, open: data.open });
          }
        }}
        modalType={removingDomain ? "modal" : "non-modal"} data-testid="policy-details-remove-domain-dialog">

        <DialogSurface data-testid="policy-details-remove-domain-dialog-surface">
          <DialogBody data-testid="policy-details-remove-domain-dialog-body">
            <DialogTitle data-testid="policy-details-remove-dialog-title">Remove {removeDomainDialog.type} Domain</DialogTitle>
            <DialogContent data-testid="policy-details-remove-domain-confirmation-dialog-content">
              Are you sure you want to remove "{removeDomainDialog.domain}" from the {removeDomainDialog.type} domains list?
              This will update the Teams policy immediately.
            </DialogContent>
            <DialogActions data-testid="policy-details-remove-domain-dialog-actions">
              <DialogTrigger disableButtonEnhancement data-testid="policy-details-remove-domain-dialog-trigger">
                <Button appearance="secondary" disabled={removingDomain} data-testid="policy-details-remove-domain-cancel-button">Cancel</Button>
              </DialogTrigger>
              <Button
                appearance="primary"
                onClick={handleRemoveDomain}
                disabled={removingDomain} data-testid="policy-details-confirm-remove-domain-button">

                {removingDomain ? 'Removing...' : 'Remove Domain'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>);

};